import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ChartFrame from "../components/ChartFrame";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart, ComposedChart } from "recharts";
import WeightStreakCard from "../components/WeightStreakCard";
import { useSiteTime } from "../lib/time";
import { Scale, Plus, Trash2, Target, TrendingDown, TrendingUp, Minus, Check, X, Pencil } from "lucide-react";
import { toast } from "sonner";

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtShort(iso, tz) {
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, month: "short", day: "numeric" }).format(new Date(iso));
}

function fmtFull(iso, tz) {
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

export default function Weight() {
  const { user, refreshUser } = useAuth();
  const { tz, tzShort } = useSiteTime();
  const [logs, setLogs] = useState([]);
  const [weight, setWeight] = useState("");
  const [loggedAt, setLoggedAt] = useState(toLocalInput(new Date()));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [goalEdit, setGoalEdit] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  const unit = user?.weight_unit || "lb";

  const saveGoal = async () => {
    const parsed = goalDraft.trim() === "" ? null : parseFloat(goalDraft);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      toast.error("Enter a positive number");
      return;
    }
    setSavingGoal(true);
    try {
      await api.patch("/users/me", { goal_weight: parsed });
      await refreshUser();
      toast.success(parsed == null ? "Goal cleared" : `Goal set to ${parsed} ${unit}`);
      setGoalEdit(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingGoal(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/weights");
      setLogs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const val = parseFloat(weight);
    if (isNaN(val) || val <= 0) { toast.error("Enter a valid weight"); return; }
    setSaving(true);
    try {
      await api.post("/weights", {
        weight: val,
        unit,
        logged_at: new Date(loggedAt).toISOString(),
        notes: notes || null,
      });
      toast.success("Weight logged");
      setWeight("");
      setNotes("");
      setLoggedAt(toLocalInput(new Date()));
      await refreshUser();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    // Snapshot before delete so we can restore on Undo.
    const snapshot = confirmDelete;
    try {
      await api.delete(`/weights/${snapshot.id}`);
      setConfirmDelete(null);
      await refreshUser();
      load();
      toast(`Removed ${Number(snapshot.weight).toFixed(1)} ${snapshot.unit || "lb"}`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/weights", {
                weight: snapshot.weight,
                unit: snapshot.unit || "lb",
                logged_at: snapshot.logged_at,
                notes: snapshot.notes || null,
              });
              await refreshUser();
              load();
              toast.success("Restored");
            } catch (err) { toast.error(formatApiError(err)); }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const chartData = useMemo(
    () => {
      const ordered = [...logs].reverse();
      if (ordered.length === 0) return [];
      // Projected trajectory: straight line from the FIRST log to goal weight at 1 lb/week
      // (or 0.45 kg/week for metric users). Clamps at the goal line.
      const first = ordered[0];
      const goalW = user?.goal_weight;
      const unit = user?.weight_unit || "lb";
      const rate = unit === "kg" ? 0.4536 : 1; // 1 lb/week
      const t0 = new Date(first.logged_at).getTime();
      const dir = goalW != null && goalW < first.weight ? -1 : goalW != null && goalW > first.weight ? 1 : 0;
      return ordered.map((l) => {
        let target = null;
        if (goalW != null && dir !== 0) {
          const days = (new Date(l.logged_at).getTime() - t0) / 86400000;
          const projected = first.weight + dir * rate * (days / 7);
          target = dir < 0 ? Math.max(goalW, projected) : Math.min(goalW, projected);
          target = Math.round(target * 10) / 10;
        }
        return {
          date: fmtShort(l.logged_at, tz),
          full: fmtFull(l.logged_at, tz),
          weight: l.weight,
          target,
        };
      });
    },
    [logs, user?.goal_weight, user?.weight_unit, tz]
  );

  const stats = useMemo(() => {
    if (logs.length < 2) return null;
    const first = logs[logs.length - 1].weight;
    const latest = logs[0].weight;
    const change = latest - first;
    const values = logs.map((l) => l.weight);
    return {
      latest,
      first,
      change,
      pct: first > 0 ? (change / first) * 100 : 0,
      min: Math.min(...values),
      max: Math.max(...values),
      entries: logs.length,
    };
  }, [logs]);

  const goal = user?.goal_weight;
  const trend = stats?.change ?? 0;
  const Direction = trend < 0 ? TrendingDown : trend > 0 ? TrendingUp : Minus;
  const dirColor = trend < 0 ? "#22c55e" : trend > 0 ? "#0D9488" : "#64748B";

  return (
    <AppLayout>
      <PageHero
        eyebrow="Body Weight"
        title="Weight Log"
        subtitle="Track your weight over time. Latest entry becomes your current body weight."
        icon={<Scale className="w-8 h-8 text-[#0D9488]" />}
        gradient
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Log form */}
        <Card className="lg:col-span-1 bg-white border border-slate-200 rounded-none p-6" data-testid="weight-log-form-card">
          {/* Goal weight editor */}
          <div className="mb-5 pb-5 border-b border-slate-200" data-testid="goal-weight-section">
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-1.5">
                <Target className="w-3 h-3 text-[#0D9488]" /> Goal Weight
              </div>
              {!goalEdit && user?.goal_weight != null && (
                <button
                  type="button"
                  onClick={() => { setGoalDraft(String(user.goal_weight)); setGoalEdit(true); }}
                  className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#0D9488] transition-colors inline-flex items-center gap-1"
                  data-testid="goal-weight-edit"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              )}
            </div>

            {goalEdit ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  autoFocus
                  value={goalDraft}
                  onChange={(e) => setGoalDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveGoal(); if (e.key === "Escape") setGoalEdit(false); }}
                  placeholder={`Target ${unit}`}
                  className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num text-lg"
                  data-testid="goal-weight-input"
                />
                <Button size="icon" onClick={saveGoal} disabled={savingGoal}
                  className="h-10 w-10 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none shrink-0"
                  data-testid="goal-weight-save">
                  <Check className="w-4 h-4" />
                </Button>
                {user?.goal_weight != null && (
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => { setGoalDraft(""); saveGoal(); }}
                    disabled={savingGoal}
                    className="h-10 w-10 border-slate-200 bg-transparent hover:bg-slate-50 hover:text-[#FF3B30] hover:border-[#FF3B30] rounded-none shrink-0"
                    aria-label="Clear goal"
                    data-testid="goal-weight-clear"
                    title="Clear goal"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <Button size="icon" variant="outline" onClick={() => setGoalEdit(false)}
                  className="h-10 w-10 border-slate-200 bg-transparent hover:bg-slate-50 rounded-none shrink-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : user?.goal_weight != null ? (
              <div>
                <div className="font-display font-black text-4xl leading-none text-[#0D9488] tabular-nums" data-testid="goal-weight-value">
                  {user.goal_weight}
                  <span className="text-slate-500 text-base ml-1 font-normal">{unit}</span>
                </div>
                {user?.body_weight != null && (
                  <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-2">
                    {Math.abs(user.body_weight - user.goal_weight) < 0.05
                      ? "on target ★"
                      : user.body_weight > user.goal_weight
                        ? `${(user.body_weight - user.goal_weight).toFixed(1)} ${unit} to lose`
                        : `${(user.goal_weight - user.body_weight).toFixed(1)} ${unit} to gain`}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setGoalDraft(""); setGoalEdit(true); }}
                className="w-full border border-dashed border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors py-3 font-mono-num text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2"
                data-testid="goal-weight-set"
              >
                <Target className="w-3.5 h-3.5" /> Set goal weight
              </button>
            )}
          </div>

          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4">// New Entry</div>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="weight-log-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Weight ({unit})</Label>
              <Input type="number" step="0.1" required value={weight} onChange={(e) => setWeight(e.target.value)}
                placeholder={user?.body_weight ? String(user.body_weight) : "e.g. 210.5"}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num text-lg" data-testid="weight-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Logged At</Label>
              <Input type="datetime-local" required value={loggedAt} onChange={(e) => setLoggedAt(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="weight-logged-at" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional context"
                className="bg-slate-50 border-slate-200 rounded-none resize-none" data-testid="weight-notes" />
            </div>
            <Button type="submit" disabled={saving}
              className="w-full h-11 bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
              data-testid="weight-save">
              <Plus className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Log Weight"}
            </Button>
          </form>
        </Card>

        {/* Chart + stats */}
        <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-none p-6" data-testid="weight-chart-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-1">// Trend</div>
              <div className="font-display font-black uppercase tracking-tight text-2xl">Weight Over Time</div>
            </div>
            {stats && (
              <div className="flex items-center gap-2" data-testid="weight-trend">
                <Direction className="w-4 h-4" style={{ color: dirColor }} />
                <span className="font-mono-num text-sm" style={{ color: dirColor }}>
                  {trend > 0 ? "+" : ""}{trend.toFixed(1)} {unit} ({stats.pct.toFixed(1)}%)
                </span>
              </div>
            )}
          </div>

          {logs.length === 0 ? (
            <div className="h-[240px] flex items-center justify-center text-slate-500 text-sm">
              Log your first entry to see the graph.
            </div>
          ) : (
            <ChartFrame className="h-[240px]" testid="weight-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <defs>
                    <linearGradient id="wgt" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0D9488" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748B" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#64748B" tick={{ fontSize: 11 }} width={40} domain={["auto", "auto"]} />
                  <Tooltip
                    formatter={(v, name) => [
                      `${v} ${unit}`,
                      name === "weight" ? "Weight" : name === "target" ? "Target trajectory" : name,
                    ]}
                  />
                  {goal != null && (
                    <ReferenceLine y={goal} stroke="#0D9488" strokeDasharray="6 4" strokeOpacity={0.5}
                      label={{ value: `Goal ${goal}`, position: "insideTopRight", fill: "#0D9488", fontSize: 11, fontFamily: "monospace" }} />
                  )}
                  {/* Projected trajectory (dashed) — where the user should be at 1 lb/week toward goal */}
                  {goal != null && chartData.some((d) => d.target != null) && (
                    <Line
                      type="monotone"
                      dataKey="target"
                      name="target"
                      stroke="#64748B"
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                  )}
                  <Area type="monotone" dataKey="weight" stroke="#0D9488" strokeWidth={2} fill="url(#wgt)" dot={{ r: 3, fill: "#0D9488" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartFrame>
          )}

          {stats && (
            <div className="grid grid-cols-4 gap-3 pt-6 border-t border-slate-200 mt-4">
              <MiniStat label="Latest" value={`${stats.latest.toFixed(1)} ${unit}`} testid="weight-stat-latest" />
              <MiniStat label="Start" value={`${stats.first.toFixed(1)} ${unit}`} testid="weight-stat-start" />
              <MiniStat label="Min" value={`${stats.min.toFixed(1)} ${unit}`} testid="weight-stat-min" />
              <MiniStat label="Max" value={`${stats.max.toFixed(1)} ${unit}`} testid="weight-stat-max" />
            </div>
          )}
        </Card>
      </div>

      {/* Streak card */}
      <div className="mt-6">
        <WeightStreakCard refreshToken={logs.length} />
      </div>

      {/* History table */}
      <div className="mt-6 bg-white border border-slate-200 rounded-none" data-testid="weight-history-card">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// History ({logs.length})</div>
          {goal != null && user?.body_weight != null && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600 font-mono-num" data-testid="weight-goal-delta">
              <Target className="w-3 h-3 text-[#0D9488]" />
              {Math.abs(user.body_weight - goal) < 0.05
                ? "Goal reached ★"
                : user.body_weight > goal
                  ? `${(user.body_weight - goal).toFixed(1)} ${unit} to goal`
                  : `${(goal - user.body_weight).toFixed(1)} ${unit} to goal`}
            </div>
          )}
        </div>
        {logs.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">No entries yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 hover:bg-transparent">
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Logged</TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Weight</TableHead>
                <TableHead className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Notes</TableHead>
                <TableHead className="text-right font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} className="border-slate-200 hover:bg-slate-50/50 transition-colors" data-testid={`weight-row-${l.id}`}>
                  <TableCell className="font-mono-num text-sm text-slate-800">{fmtFull(l.logged_at, tz)}</TableCell>
                  <TableCell className="font-mono-num text-slate-900">{l.weight} {l.unit || unit}</TableCell>
                  <TableCell className="text-slate-600 text-sm max-w-md truncate">{l.notes || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(l)}
                      className="h-8 w-8 hover:bg-slate-200 text-slate-600 hover:text-[#FF3B30] rounded-none"
                      data-testid={`weight-delete-${l.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will permanently remove this weight log entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="weight-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="weight-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="border-l border-slate-200 pl-3" data-testid={testid}>
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</div>
      <div className="font-display font-black text-xl leading-none">{value}</div>
    </div>
  );
}
